import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import SearchIcon from "../../assets/svg/search-icon";
import UseCaseItem from "./components/UseCaseItem";
import BranchesSubDropdown from "./components/BranchesSubDropdown";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";

export interface BranchOption {
  id: string;
  name: string;
  disabled?: boolean;
}

export interface UseCaseOption {
  id: string;
  name: string;
  branches?: BranchOption[];
  disabled?: boolean;
}

export interface CampaignBranchesDropdownProps {
  useCases: UseCaseOption[];
  selectedUseCases: string[];
  selectedBranches: Map<string, string[]>;
  onSelectionChange: (
    useCases: string[],
    branches: Map<string, string[]>
  ) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
}

export interface UseCaseItemProps {
  useCase: UseCaseOption;
  isSelected: boolean;
  selectedBranchCount: number;
  totalBranchCount: number;
  isFocused: boolean;
  onToggle: () => void;
  onBranchesClick: () => void;
  onMouseEnter: () => void;
  hasBranches: boolean;
}

export interface BranchesSubDropdownProps {
  useCase: UseCaseOption;
  selectedBranches: string[];
  onBranchToggle: (branchId: string) => void;
  onClose: () => void;
  className?: string;
}

const CampaignBranchesDropdown: React.FC<CampaignBranchesDropdownProps> = ({
  useCases = [],
  selectedUseCases = [],
  selectedBranches = new Map(),
  onSelectionChange,
  id,
  placeholder = "Select Use Case / Branch",
  disabled = false,
  className = "",
  triggerClassName = "",
  dropdownClassName = "",
  searchPlaceholder = "Search",
  showSearch = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedUseCase, setExpandedUseCase] = useState<UseCaseOption | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const dropdownId = id || `campaign-branches-dropdown-${Math.random().toString(36).slice(2, 9)}`;

  // Filter use cases based on search
  const filteredUseCases = useMemo(() => {
    if (!searchQuery.trim()) return useCases;
    return useCases.filter((uc) =>
      uc.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [useCases, searchQuery]);

  // Get display text for trigger
  const displayText = useMemo(() => {
    if (selectedUseCases.length === 0) return placeholder;

    const selectedNames = useCases
      .filter((uc) => selectedUseCases.includes(uc.id))
      .map((uc) => uc.name);

    if (selectedNames.length === 1) return selectedNames[0];
    return `${selectedNames.length} selected`;
  }, [selectedUseCases, useCases, placeholder]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setFocusedIndex(-1);
      setSearchQuery("");
      setExpandedUseCase(null);
      if (!isOpen && showSearch) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }
  }, [disabled, isOpen, showSearch]);

  const handleUseCaseToggle = useCallback(
    (useCase: UseCaseOption) => {
      const isCurrentlySelected = selectedUseCases.includes(useCase.id);
      const newSelectedUseCases = isCurrentlySelected
        ? selectedUseCases.filter((id) => id !== useCase.id)
        : [...selectedUseCases, useCase.id];

      const newSelectedBranches = new Map(selectedBranches);
      if (isCurrentlySelected) {
        // Deselecting: remove branch selections for this use case
        newSelectedBranches.delete(useCase.id);
      } else {
        // Selecting: auto-select all branches for this use case
        if (useCase.branches && useCase.branches.length > 0) {
          newSelectedBranches.set(
            useCase.id,
            useCase.branches.map((branch) => branch.id)
          );
        }
      }

      onSelectionChange(newSelectedUseCases, newSelectedBranches);
    },
    [selectedUseCases, selectedBranches, onSelectionChange]
  );

  const handleBranchToggle = useCallback(
    (useCaseId: string, branchId: string) => {
      const currentBranches = selectedBranches.get(useCaseId) || [];
      const newBranches = currentBranches.includes(branchId)
        ? currentBranches.filter((id: string) => id !== branchId)
        : [...currentBranches, branchId];

      const newSelectedBranches = new Map(selectedBranches);
      if (newBranches.length > 0) {
        newSelectedBranches.set(useCaseId, newBranches);
      } else {
        newSelectedBranches.delete(useCaseId);
      }

      // Auto-select use case if branches are selected
      const newSelectedUseCases = newBranches.length > 0 && !selectedUseCases.includes(useCaseId)
        ? [...selectedUseCases, useCaseId]
        : [...selectedUseCases];

      onSelectionChange(newSelectedUseCases, newSelectedBranches);
    },
    [selectedUseCases, selectedBranches, onSelectionChange]
  );

  const handleBranchesClick = useCallback((useCase: UseCaseOption) => {
    setExpandedUseCase((prev) => (prev?.id === useCase.id ? null : useCase));
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
        setSearchQuery("");
        setExpandedUseCase(null);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className={`relative ${className}`}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        id={`${dropdownId}-trigger`}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        className={`
          select-none cursor-pointer rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk 
          border border-border-gray dark:border-border-gray-dk backdrop-blur-[10px]
          transition-colors focus:outline-none flex items-center justify-between
          px-[8px] py-[7px] gap-[8px]
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          ${triggerClassName}
        `}
        onClick={handleToggle}
      >
        <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk truncate">
          {displayText}
        </span>
        <ChevronDownIcon
          className={`w-[8px] h-[8px] shrink-0 text-heading-text dark:text-heading-text-dk transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Dropdown */}
      <div
        ref={dropdownRef}
        id={`${dropdownId}-listbox`}
        role="listbox"
        aria-multiselectable="true"
        className={`
          absolute top-full left-[-50px] z-[9999] mt-1 w-[250px] 
          bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk 
          rounded-lg shadow-popper-blur-effect backdrop-blur-[20px] 
          transition-all duration-200 ease-out overflow-visible
          ${
            isOpen
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-2 pointer-events-none"
          }
          ${dropdownClassName}
        `}
      >
        {/* Search Input */}
        {showSearch && (
          <div className="px-2 py-2 flex items-center gap-1.5 border-b border-border-gray dark:border-border-gray-dk">
            <SearchIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-xs bg-transparent border-none outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Use Cases List */}
        <div
          className={`max-h-[120px] overflow-y-auto overflow-x-visible ${visibleScrollbar(
            "[&::-webkit-scrollbar]:w-[6px]"
          )}`}
        >
          {filteredUseCases.length === 0 ? (
            <div className="px-3 py-3 text-xs text-gray-placeholder dark:text-gray-placeholder-dk">
              No use cases found
            </div>
          ) : (
            filteredUseCases.map((useCase, index) => {
              const hasBranches = (useCase.branches?.length || 0) > 0;
              const branchesForUseCase = selectedBranches.get(useCase.id) || [];

              return (
                <div key={useCase.id} className="relative">
                  <UseCaseItem
                    useCase={useCase}
                    isSelected={selectedUseCases.includes(useCase.id)}
                    selectedBranchCount={branchesForUseCase.length}
                    totalBranchCount={useCase.branches?.length || 0}
                    isFocused={index === focusedIndex}
                    onToggle={() => handleUseCaseToggle(useCase)}
                    onBranchesClick={() => handleBranchesClick(useCase)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    hasBranches={hasBranches}
                  />
                </div>
              );
            })
          )}
        </div>

        {/* Branches Sub-dropdown - rendered outside the scrollable list */}
        {expandedUseCase && (expandedUseCase.branches?.length || 0) > 0 && (
          <BranchesSubDropdown
            useCase={expandedUseCase}
            selectedBranches={selectedBranches.get(expandedUseCase.id) || []}
            onBranchToggle={(branchId) =>
              handleBranchToggle(expandedUseCase.id, branchId)
            }
            onClose={() => setExpandedUseCase(null)}
          />
        )}
      </div>
    </div>
  );
};

export default CampaignBranchesDropdown;